import React from 'react';
import { Typography } from 'antd';
import cn from 'classnames';
import { Table } from '../../../base/Table';
import Box from '../../../../shared/ui/Box';
import { createHouseAddress } from '../../../../shared/packages/address';
import TableTags from '../../../../shared/ui/TableTags';
import Tag from '../../../../shared/ui/Tag';
import { getDeclRoom } from './utils';
import { ReactComponent as ExpertIcon } from '../../../../../../resource/AcceptanceExpertIcon.svg';
import { dateFormats, dateTime } from '../../../../shared/packages/dateTime';
import { hideService } from '../../utils/hideService';
import { createIsExpandedSelector } from '../../utils/selectors';
import { useStateControllers, useStateSelector } from '../../stateManager';
import { stateKeys } from '../../constants/state';
import { useAcceptance } from '../../services/acceptanceProvider';
import styles from '../index.module.scss';

export const RoomNumberCell = ({ record, column, row = 1 }) => {
  return (
    <NumberCell column={column} row={row} type="first">
      {record.flat}
    </NumberCell>
  );
};

export const AddressDduCell = ({ record, column, row }) => {
  if (!record.addressDdu) {
    return (
      <Table.BodyCell column={column} row={row}>
        <Typography.Text>—</Typography.Text>
      </Table.BodyCell>
    );
  }

  return (
    <NumberCell column={column} row={row}>
      {record.addressDdu}
    </NumberCell>
  );
};

export const NumberDduCell = ({ record, column, row }) => {
  if (!record.numberDdu) {
    return (
      <Table.BodyCell column={column} row={row}>
        <Typography.Text>—</Typography.Text>
      </Table.BodyCell>
    );
  }

  return (
    <NumberCell column={column} row={row}>
      {record.numberDdu}
    </NumberCell>
  );
};

export const AddressCell = ({ record, column, row }) => {
  return (
    <Table.BodyCell column={column} row={row} type="first">
      <Box square className={styles.address}>{`${createHouseAddress(record)}, ${
        record.flat
      }`}</Box>
    </Table.BodyCell>
  );
};

export const FinishTypeCell = ({ record, column, row }) => {
  return (
    <Table.BodyCell column={column} row={row}>
      <Typography.Text>
        {record.addressTypeOfFinish?.name ?? '—'}
      </Typography.Text>
    </Table.BodyCell>
  );
};

export const RelationCell = ({ record, column, row }) => {
  const relLength = record.relatedFlats?.length || 0;
  const hasRelation = Boolean(relLength);

  return (
    <Table.BodyCell column={column} row={row}>
      <TableTags>
        <Tag type={Tag.types.base}>
          {hasRelation ? `${relLength} ${getDeclRoom(relLength)}` : 'Нет'}
        </Tag>
        {hasRelation && <ToggleRowExpand roomId={record.id} />}
      </TableTags>
    </Table.BodyCell>
  );
};

export const RoomTypeCell = ({ record, column, row }) => {
  return (
    <Table.BodyCellWithDefault
      renderCondition={Boolean(record.typeName)}
      column={column}
      row={row}
    >
      <Box>{record.typeName}</Box>
    </Table.BodyCellWithDefault>
  );
};

export const ServiceCell = ({ record, column, row }) => {
  if (hideService(record)) {
    return null;
  }

  const withExpert = record.recordInfo?.isExpert;
  const byProxy = record.recordInfo?.byProxy;

  return (
    <Table.BodyCellWithDefault
      renderCondition={Boolean(record.recordInfo)}
      column={column}
      row={row}
    >
      <TableTags>
        <Tag type={Tag.types.base}>
          {withExpert ? 'Прием с экспертом' : 'Прием без эксперта'}
          {withExpert && <ExpertIcon />}
        </Tag>
        <Tag type={Tag.types.baseInverted}>
          {byProxy ? 'По доверенности' : 'Без доверенности'}
        </Tag>
      </TableTags>
    </Table.BodyCellWithDefault>
  );
};

export const DeskCell = ({ record, column, row }) => {
  if (hideService(record)) {
    return null;
  }

  return (
    <Table.BodyCellWithDefault
      renderCondition={Boolean(record.recordInfo?.acceptanceDesk?.name)}
      column={column}
      row={row}
    >
      <TableTags>
        <Tag type={Tag.types.base}>
          {record.recordInfo?.acceptanceDesk?.name}
        </Tag>
      </TableTags>
    </Table.BodyCellWithDefault>
  );
};

export const IndividualStartDateCell = ({ record, column, row }) => {
  const { individualStartDate } = record;

  const acceptance = useAcceptance();

  const fromDate = acceptance?.fromDate
    ? dateTime(acceptance.fromDate)
    : undefined;

  const startDate = individualStartDate
    ? dateTime(individualStartDate)
    : undefined;

  const danger = startDate && fromDate && startDate.isBefore(fromDate, 'day');

  return (
    <Table.BodyCellWithDefault
      renderCondition={Boolean(startDate)}
      column={column}
      row={row}
    >
      <DateContent danger={danger}>
        {startDate?.format(dateFormats.DMY)}
      </DateContent>
    </Table.BodyCellWithDefault>
  );
};

export const IndividualEndDateCell = ({ record, column, row }) => {
  const { individualEndDate } = record;
  const acceptance = useAcceptance();

  const toDate = acceptance?.toDate ? dateTime(acceptance.toDate) : undefined;
  const endDate = individualEndDate ? dateTime(individualEndDate) : undefined;

  const danger = endDate && toDate && endDate.isAfter(toDate, 'day');

  return (
    <Table.BodyCellWithDefault
      renderCondition={Boolean(endDate)}
      column={column}
      row={row}
    >
      <DateContent danger={danger}>
        {endDate?.format(dateFormats.DMY)}
      </DateContent>
    </Table.BodyCellWithDefault>
  );
};

function ToggleRowExpand({ roomId }) {
  const selectIsExpanded = createIsExpandedSelector(roomId);
  const expanded = useStateSelector(selectIsExpanded);

  const { setValue } = useStateControllers();

  const handleClick = () => {
    setValue(stateKeys.expandedRowKeys, (prev) => {
      return expanded ? prev.filter((id) => id !== roomId) : [...prev, roomId];
    });
  };

  return (
    <Tag type={Tag.types.baseInverted} onClick={handleClick}>
      {expanded ? 'Свернуть' : 'Развернуть'}
    </Tag>
  );
}

function DateContent({ children, danger }) {
  return (
    <span className={cn(styles.date, { [styles.danger]: danger })}>
      {children}
    </span>
  );
}

function NumberCell({ children, ...props }) {
  return (
    <Table.BodyCell {...props}>
      <Box square className={styles.roomNumber}>
        {children}
      </Box>
    </Table.BodyCell>
  );
}
